import { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import Iconify from 'src/components/Iconify';

// ----------------------------------------------------------------------
// Primitivos de UI para blocos de código copiáveis, reaproveitados pelas
// abas "Identificação" e "Instalação" do canal/projeto webchat.

export function CopyButton({ text }) {
    const { t } = useTranslation('settings');
    const [copied, setCopied] = useState(false);
    return (
        <button
            type="button"
            onClick={() => {
                navigator.clipboard.writeText(text);
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
            }}
            className="flex items-center gap-1.5 px-3 py-2 rounded-xl border border-gray-200 text-sm text-gray-700 hover:bg-gray-50 transition-colors whitespace-nowrap"
        >
            <Iconify
                icon={copied ? 'eva:checkmark-outline' : 'eva:copy-outline'}
                width={16}
                className={copied ? 'text-green-600' : 'text-gray-500'}
            />
            {copied ? t('channels.webchat.copyCode.copiedLabel', 'Copiado') : t('buttons.copy', { ns: 'common' })}
        </button>
    );
}

CopyButton.propTypes = { text: PropTypes.string };

export function CodeBlock({ code }) {
    const { t } = useTranslation('settings');
    const [copied, setCopied] = useState(false);
    return (
        <div className="relative">
            <pre className="bg-slate-900 text-slate-100 rounded-b-xl rounded-tr-xl p-4 pr-14 text-xs font-mono overflow-x-auto leading-relaxed">
                {code}
            </pre>
            <button
                type="button"
                onClick={() => {
                    navigator.clipboard.writeText(code);
                    setCopied(true);
                    setTimeout(() => setCopied(false), 2000);
                }}
                className="absolute top-2 right-2 w-8 h-8 flex items-center justify-center rounded-lg bg-slate-800 text-slate-300 hover:bg-slate-700 transition-colors"
                aria-label={t('channels.webchat.copyCode.copyCodeAriaLabel', 'Copiar código')}
            >
                <Iconify icon={copied ? 'eva:checkmark-outline' : 'eva:copy-outline'} width={16} className={copied ? 'text-green-400' : ''} />
            </button>
        </div>
    );
}

CodeBlock.propTypes = { code: PropTypes.string };

// Abas de código por linguagem/framework. `tabs` = [{ id, label, code }].
export function CodeTabs({ tabs }) {
    const [active, setActive] = useState(tabs[0]?.id);
    const current = tabs.find((t) => t.id === active) || tabs[0];
    return (
        <div>
            <div className="flex items-center gap-1 flex-wrap">
                {tabs.map((t) => (
                    <button
                        key={t.id}
                        type="button"
                        onClick={() => setActive(t.id)}
                        className={`px-3 py-1.5 text-xs font-medium rounded-t-lg border-b-2 transition-colors ${
                            (current?.id === t.id)
                                ? 'bg-slate-900 text-white border-[#7C3AED]'
                                : 'bg-slate-100 text-slate-500 border-transparent hover:text-slate-700'
                        }`}
                    >
                        {t.label}
                    </button>
                ))}
            </div>
            <CodeBlock code={current?.code || ''} />
        </div>
    );
}

CodeTabs.propTypes = { tabs: PropTypes.array };
